/*
 * Cọc chờ thu trên màn POS: thu ngân thấy lệnh Kế toán đã đẩy và xác nhận đã thu.
 * Giả lập bằng localStorage vì chưa có API: chỉ thấy trên cùng trình duyệt với máy Kế toán, chưa tạo đơn thật trên POS.
 * Đặt ngoài pages/POS (gắn ở route /pos trong App.tsx) để không đụng giao diện POS. Có API thì đọc hàng chờ từ server, giữ kết quả dạng PosDepositReport.
 */
import { useMemo, useState } from 'react';
import { Alert, Button, Drawer, Empty, Popconfirm, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { Banknote } from 'lucide-react';
import { usePosAuth } from '../POS/utils';
import { posQueueRows, posSimReport, type DepositRow } from './lead/deposit';
import { money } from './lead/quote';
import { useLeadStore } from './lead/store';

const maskPhone = (p?: string | null) => String(p ?? '').replace(/(\d{3})\d+(\d{4})/, '$1***$2');

const Line = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-baseline justify-between gap-3 text-[13px]">
    <span className="shrink-0 text-gray-500">{label}</span>
    <span className="min-w-0 text-right text-gray-800 dark:text-gray-100">{children}</span>
  </div>
);

export default function PosDepositDock() {
  const { can, userName } = usePosAuth();
  const byId = useLeadStore((s) => s.byId);
  const depositAct = useLeadStore((s) => s.depositAct);
  const rows = useMemo(() => posQueueRows(byId).sort((a, b) => b.deposit.order!.at - a.deposit.order!.at), [byId]);
  const [open, setOpen] = useState(false);

  const confirm = (r: DepositRow) => {
    const err = depositAct(r.leadId, r.quote.id, { type: 'pos_report', report: posSimReport(r.deposit, userName) }, userName);
    if (err) message.error(err);
    else message.success(`Đã thu cọc ${r.deposit.code}, đã báo về Kế toán và Tư vấn`);
  };

  return (
    <>
      {rows.length > 0 && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-4 left-4 z-[1100] flex h-11 items-center gap-2 rounded-full bg-[#2c6b7f] px-4 text-sm font-semibold text-white shadow-lg hover:bg-[#357f96]"
        >
          <Banknote size={18} /> Cọc chờ thu ({rows.length})
        </button>
      )}
      <Drawer open={open} onClose={() => setOpen(false)} title={`Cọc chờ thu (${rows.length})`} width={460}>
        <Alert type="warning" showIcon className="!mb-3 text-[12.5px]" message="Đang giả lập trên trình duyệt này, chưa có API. Máy POS khác chưa thấy lệnh, và xác nhận ở đây chưa tạo đơn trên POS." />
        {rows.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có khoản cọc nào chờ thu" />}
        <div className="space-y-3">
          {rows.map((r) => {
            const { deposit: d, quote: q } = r;
            const o = d.order!;
            return (
              <div key={d.code} data-testid={`pos-deposit-${d.code.slice(0, 2)}`} className="space-y-1.5 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-gray-50">{d.cust?.name ?? `KH ${r.leadId}`}</div>
                    <div className="text-[12px] text-gray-500">{d.cust?.code}{d.cust?.phone ? ` · ${maskPhone(d.cust.phone)}` : ''}</div>
                  </div>
                  <Tag color="cyan" className="!m-0 font-mono">{o.code}</Tag>
                </div>
                <Line label="Mã cọc · báo giá"><span className="font-mono text-[12px]">{d.code} · {q.code}</span></Line>
                <Line label="Dịch vụ">{o.items.join(' + ') || '—'}</Line>
                <Line label="Tổng đơn"><b>{money(o.total)}</b></Line>
                <Line label="Tiền cọc (chuyển khoản)"><b className="text-green-600">{money(d.amount)}</b></Line>
                <Line label="Còn lại sau cọc"><b className="text-[#2c6b7f]">{money(Math.max(0, o.total - d.amount))}</b></Line>
                <Line label="Mã sao kê"><span className="font-mono text-[12px]">{d.bankRef || 'Không có'}</span></Line>
                <Line label="Kế toán đẩy">{o.by} · {dayjs(o.at).format('HH:mm DD/MM')}</Line>
                {can('create') ? (
                  <Popconfirm
                    title={`Xác nhận đã thu cọc ${money(d.amount)}?`}
                    description={<span className="text-[12.5px]">Kế toán thấy còn nợ {money(Math.max(0, o.total - d.amount))}, Tư vấn chỉ lập kế hoạch cho phần này.</span>}
                    okText="Đã thu cọc"
                    cancelText="Không"
                    onConfirm={() => confirm(r)}
                  >
                    <Button type="primary" block className="!mt-2 !bg-[#357f96]">Xác nhận đã thu cọc</Button>
                  </Popconfirm>
                ) : <div className="pt-1 text-[12px] text-gray-400">Tài khoản này không có quyền thu tiền trên POS</div>}
              </div>
            );
          })}
        </div>
      </Drawer>
    </>
  );
}
